// Converts a string to lowercase, trims it, and replaces spaces with underscores.

import { initialFormAccessRightsState } from "../constants/accessRights";

// Example: "  The Goat plim " → "the_goat_plim"
export const toLowerUnderscore = (text = "") => {
  if (typeof text !== "string") return "";

  return text
    .trim()                  // remove leading/trailing spaces
    .toLowerCase()           // convert to lowercase
    .replace(/\s+/g, "_")    // replace one or more spaces with underscore
    .concat("_file");
};

export const isNormalizedMatch = (value = "", target) => {
  const normalize = (text) =>
    text?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

  return normalize(value) === normalize(target);
};

export const formatCurrency = (value, withPhp = true) => {
  const formatted = (Number(value) || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return withPhp ? `${formatted}` : formatted;
};


// Transform the data from {name, value} to {label, value} for enums
export const transformEnumData = (data) => {
  return data.map(item => ({
    value: item.value,
    label: item.name.replace(/_/g, ' ') // Convert underscores to spaces
  }));
};

export const transformEnumCapitalizeData = (data) => {
  return data.map(item => ({
    value: item.value,
    label: item.name
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
  }));
};

// Transform shift schedule data
export const transformShiftData = (data) => {
  return data.map(item => ({
    value: item.id,
    label: item.shiftName || `Shift ${item.id}`
  }));
};

// Simple function to map overtime type to transaction enum using fetched data
export const mapOvertimeTypeToTransactionEnum = (overtimeType, transactionTypes) => {
  if (!transactionTypes || !transactionTypes.length) {
    console.warn('Transaction types not loaded yet');
    return null;
  }

  // Map the readable names to your enum names
  const nameMapping = {
    "Regular": "OT_REGULAR",
    "Restday": "OT_RESTDAY",
    "SpecialHoliday": "OT_HOLIDAY_SPECIAL",
    "RegularHoliday": "OT_HOLIDAY_REGULAR",
    "RestdaySpecialHoliday": "OT_RESTDAY_HOLIDAY_SPECIAL",
    "RestdayRegularHoliday": "OT_RESTDAY_HOLIDAY_REGULAR"
  };

  const enumName = nameMapping[overtimeType];
  if (!enumName) {
    console.warn(`No mapping found for overtime type: ${overtimeType}`);
    return null;
  }

  // Find the enum in the fetched transaction types
  const foundEnum = transactionTypes.find(item => item.name === enumName);

  if (!foundEnum) {
    console.warn(`Enum ${enumName} not found in fetched transaction types`);
    return null;
  }

  return foundEnum.value;
};

export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

export const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};


export const formatTime = (timeStr) => {
  if (!timeStr) return "-";
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export const formatDateTime = (datetimeStr) => {
  if (!datetimeStr) return "";

  const date = new Date(datetimeStr);

  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  return formatter.format(date);
};

// result : eg. January 20, 1997
export function formatDateLong(dateString) {
  const date = new Date(dateString);

  const utcTime = date.getTime() - (date.getTimezoneOffset() * 60000);
  const utcDate = new Date(utcTime);

  const options = {
    month: "long",
    day: "2-digit",
    year: "numeric"
  };

  return utcDate.toLocaleDateString("en-US", options);
}


// ADDED: For <input type="datetime-local" /> (Format: YYYY-MM-DDTHH:mm)
// used in fixed logs
export const formatDateTimeForLocalInput = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// used in CVT bulk logs upload
export const formatDateTimeForUpload = (date) => {
  if (!date || isNaN(date.getTime())) return null;

  // Use toLocaleString with a specific format or simply let JSON.stringify 
  // handle the date object. But to be safe with C# DateTimeOffset:
  const pad = (num) => String(num).padStart(2, '0');
  const o = -date.getTimezoneOffset();
  const sign = o >= 0 ? '+' : '-';
  const offset = `${sign}${pad(Math.floor(Math.abs(o) / 60))}:${pad(Math.abs(o) % 60)}`;

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`;
};

// ADDED: For cleaner Table display (Format: Oct 27, 2023, 02:30 PM)
export const formatDateTimeShort = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true
  }).format(date);
};


export const getDateNow = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


export function findKeyByModuleName(moduleName) {
  // Loop through each category in initialFormAccessRightsState
  for (const category in initialFormAccessRightsState) {
    // Find the item with the matching name
    const foundItem = initialFormAccessRightsState[category].find(
      item => item.name === moduleName
    );

    // If found, return the key
    if (foundItem) {
      return foundItem.key;
    }
  }

  // Return null if not found
  return null;
}

export function hasModuleAccess(userAccess, moduleName) {
  const key = findKeyByModuleName(moduleName);

  const access = key && userAccess?.[key];

  return access || false;
}


// Display things
export const getSelectStyle = (hasValue, isEditMode) => ({
  width: "100%",
  height: "35px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  backgroundColor: !isEditMode ? "#eee" : "white",
  // If it has a value, make it Dark (#333), otherwise make it Gray (#757575)
  color: hasValue ? "#535252" : "#757575"
});

export const predictSalaryAdvanceEndDate = (
  loanAmount,
  deductionAmount,
  startDate,
  deductionFrequency
) => {
  let remainingBalance = Number(loanAmount);
  let currentDate = new Date(startDate);
  let lastDeductionDate = new Date(currentDate);

  while (remainingBalance > 0.0001) {
    lastDeductionDate = new Date(currentDate);

    const amountToCharge =
      remainingBalance <= deductionAmount
        ? remainingBalance
        : deductionAmount;

    remainingBalance -= amountToCharge;

    if (remainingBalance > 0.0001) {
      currentDate = getNextCutoffDate(currentDate, deductionFrequency);
    }
  }

  // --- SQL SERVER FORMATTING START ---
  const year = lastDeductionDate.getFullYear();
  // Months are 0-indexed, so add 1. padStart ensures 2 digits (e.g. 05 instead of 5)
  const month = String(lastDeductionDate.getMonth() + 1).padStart(2, "0");
  const day = String(lastDeductionDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // Returns "YYYY-MM-DD"
  // --- SQL SERVER FORMATTING END ---
};


const getNextCutoffDate = (currentDate, frequency) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  if (frequency === "Every Cutoff") {
    // 1st -> 16th of same month
    if (currentDate.getDate() < 16) {
      return new Date(year, month, 16);
    }

    // 16th -> 1st of next month
    return new Date(year, month + 1, 1);
  }

  if (frequency === "First Cutoff") {
    // Always 1st of next month
    return new Date(year, month + 1, 1);
  }

  if (frequency === "Second Cutoff") {
    // Always 16th of next month
    return new Date(year, month + 1, 16);
  }

  throw new Error("Invalid deduction frequency");
};